import { Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Search,
  FileText,
  Building2,
  HardHat,
  MapPin,
  UserPlus,
  CalendarPlus,
  Trash2,
  Wifi,
  Circle,
  Wand2,
  LayoutTemplate,
  PenLine,
  ChevronDown,
  ChevronUp,
  Activity,
  MousePointerClick,
  Eye,
  Navigation,
  Smartphone,
  Tablet,
  Monitor,
  FileDown,
  Ban,
  ShieldOff,
  StickyNote,
  Check,
  X,
  Mail,
  Sparkles,
  RefreshCw,
  ArrowRightLeft,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, Fragment, useRef, useEffect } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { AdminComposeEmailDialog, type EmailRecipient } from "@/components/admin-compose-email-dialog";
import { AdminSubscriptionTransferDialog, type TransferSource } from "@/components/admin-subscription-transfer-dialog";

interface AdminUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  authProvider: string | null;
  referralCode: string | null;
  referredByUserId: string | null;
  referredByName: string | null;
  referredByEmail: string | null;
  subscriptionStatus: string | null;
  trialStatus: string | null;
  trialStartAt: string | null;
  trialEndAt: string | null;
  createdAt: string | null;
  lastSeenAt: string | null;
  companyName: string | null;
  region: string | null;
  quoteCount: number;
  generatedAiQuoteCount: number;
  templateQuoteCount: number;
  manualQuoteCount: number;
  claimedTestQuoteCount: number;
  quotaLimitEnforced: boolean;
  adminNotes: string | null;
  bonusQuotaCredits: number;
  stripeSubscriptionId: string | null;
  reengagementEmailSentAt: string | null;
  reengagementEmailOpenedAt: string | null;
  firstQuoteNudgeEmailSentAt: string | null;
  firstQuoteNudgeEmailOpenedAt: string | null;
  firstQuoteNudgeEmailClickedAt: string | null;
  trialExtensionEmailOpenedAt: string | null;
  trialExtensionEmailClickedAt: string | null;
  products?: string[];
}

type ProductFilter = "builder" | "engineer";

interface UserActivityEvent {
  id: string;
  userId: string;
  eventType: string;
  page: string | null;
  eventData: Record<string, unknown> | null;
  createdAt: string;
}

interface GuestQuoteJourney {
  id: string;
  event: "Test Quote Generated";
  status: "anonymous" | "claimed";
  createdAt: string;
  userId: string | null;
  userName: string;
  userEmail: string | null;
}

function timeSince(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const ms = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function pageLabel(page: string | null): string {
  if (!page) return "";
  const labels: Record<string, string> = {
    "/quotes/new": "Smart Quote",
    "/quotes/template": "Template Quote",
    "/quotes/manual": "Manual Quote",
    "/dashboard": "Dashboard",
    "/quote-ai": "Smart Quote",
    "/quote-manual": "Manual Quote",
    "/quote-edit": "Edit Quote",
    "/quotes": "Quotes",
    "/settings": "Settings",
    "/pricing": "Pricing",
    "/clients": "Clients",
    "/referrals": "Referrals",
  };
  if (labels[page]) return labels[page];
  if (page.startsWith("/quote-edit")) return "Edit Quote";
  return page.replace(/^\//, "").replace(/-/g, " ");
}

function eventIcon(event: UserActivityEvent) {
  if (event.eventType === "page_view") return <Eye className="h-3 w-3 text-muted-foreground" />;
  if (event.eventType === "quote_started") return <MousePointerClick className="h-3 w-3 text-primary" />;
  if (event.eventType === "quote_generated") {
    const type = event.eventData?.type as string | undefined;
    if (type === "manual" || type === "template") return <FileText className="h-3 w-3 text-primary" />;
    return <Wand2 className="h-3 w-3 text-primary" />;
  }
  if (event.eventType === "pdf_downloaded") return <FileDown className="h-3 w-3 text-primary" />;
  return <Navigation className="h-3 w-3 text-muted-foreground" />;
}

function deviceIcon(device: string | undefined) {
  if (device === "mobile") return <Smartphone className="h-3 w-3 text-muted-foreground/60" />;
  if (device === "tablet") return <Tablet className="h-3 w-3 text-muted-foreground/60" />;
  if (device === "desktop") return <Monitor className="h-3 w-3 text-muted-foreground/60" />;
  return null;
}

function eventLabel(event: UserActivityEvent): string {
  const journeyLabels: Record<string, string> = {
    description_create_clicked: "Clicked Create Description",
    quote_example_selected: "Selected an example project",
    quote_generate_clicked: "Clicked Create Quote",
    description_started: "Description generation started",
    description_completed: "Description generation completed",
    description_request_started: "Browser started description request",
    description_result_accepted: "Description received and ready in browser",
    description_result_discarded: "Description result kept from replacing newer edits",
    description_request_failed: "Browser could not complete description request",
    description_request_not_started: "Description request needs project details",
    description_confirmation_opened: "Opened description confirmation",
    description_confirmation_cancelled: "Cancelled description confirmation",
    generated_description_edited: "Edited generated description",
    measurements_confirmed: "Confirmed measurements",
    project_draft_save_failed: "Browser could not keep project progress",
    quote_request_started: "Browser started quotation request",
    quote_result_accepted: "Quotation received in browser",
    quote_client_failed: "Browser could not complete quotation request",
    description_failed: "Description generation failed",
    description_interrupted: "Description connection interrupted",
    quote_analysis_started: "Quote generation started",
    quote_analysis_completed: "Quote generation request completed",
    quote_analysis_failed: "Quote generation failed",
    quote_analysis_interrupted: "Quote generation connection interrupted",
    quote_generation_blocked: "Quote needs an earlier step",
  };
  if (journeyLabels[event.eventType]) {
    const reasons: Record<string, string> = {
      provider_timeout: "AI service timed out",
      response_limit: "AI response was cut off",
      provider_busy: "AI service unavailable",
      generation_failed: "Generation could not complete",
      project_information_required: "Add drawings or a description",
      description_required: "Create the description first",
      measurement_confirmation_required: "Confirm measurements first",
      connection_closed: "Browser connection closed; generation outcome unknown",
    };
    const reason = event.eventData?.reason as string | undefined;
    const reference = event.eventData?.requestId as string | undefined;
    return `${journeyLabels[event.eventType]}${reason ? ` — ${reasons[reason] || reason}` : ""}${reference ? ` (ref ${reference})` : ""}`;
  }
  if (event.eventType === "page_view") {
    const pg = (event.eventData?.page as string) || pageLabel(event.page) || event.page || "Unknown page";
    return `Visited ${pg}`;
  }
  if (event.eventType === "quote_started") {
    const type = (event.eventData?.type as string) || "";
    if (type === "ai") return "Started Smart Quote";
    if (type === "manual") return "Started Manual Quote";
    if (type === "template") return "Started Template Quote";
    return "Started a quote";
  }
  if (event.eventType === "quote_generated") {
    const type = event.eventData?.type as string | undefined;
    const count = event.eventData?.lineItemCount as number | undefined;
    if (type === "manual") return "Saved Manual Quote";
    if (type === "template") return "Saved Template Quote";
    return count ? `Generated Smart Quote (${count} items)` : "Generated Smart Quote";
  }
  if (event.eventType === "pdf_downloaded") {
    const qn = event.eventData?.quoteNumber as string | undefined;
    return qn ? `Downloaded PDF (${qn})` : "Downloaded PDF";
  }
  return event.eventType.replace(/_/g, " ");
}

export function UserActivitySection({ userId }: { userId: string }) {
  const { data: events = [], isLoading, isError, refetch } = useQuery<UserActivityEvent[]>({
    queryKey: ["/api/admin/users", userId, "activity"],
    queryFn: () =>
      fetch(`/api/admin/users/${userId}/activity`, { credentials: "include" })
        .then(async (r) => {
          if (!r.ok) throw new Error("Failed to load activity");
          const events = await r.json();
          if (!Array.isArray(events)) throw new Error("Invalid activity response");
          return events;
        }),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  if (isLoading) {
    return (
      <div className="mt-3 pt-3 border-t">
        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
          <Activity className="h-3.5 w-3.5" />
          Recent activity
        </p>
        <div className="space-y-1.5">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-4 w-full" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t" data-testid={`section-activity-${userId}`}>
      <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
        <Activity className="h-3.5 w-3.5" />
        Recent activity
        {(!isError || events.length > 0) && (
          <span className="text-muted-foreground font-normal">({events.length} events)</span>
        )}
      </p>
      <p className="text-xs text-muted-foreground mb-2">
        Recorded page visits and actions, separate from browser connection signals.
      </p>
      {isError && (
        <div role="alert" className="text-xs mb-2">
          <span>{events.length ? "Activity could not refresh. Entries may be out of date." : "Could not load recorded activity."}</span>
          <Button variant="ghost" size="sm" onClick={() => void refetch()} className="ml-2 h-7 text-xs">
            Retry activity
          </Button>
        </div>
      )}
      {events.length === 0 && !isError ? (
        <p className="text-xs text-muted-foreground italic">No activity recorded yet.</p>
      ) : (
        <div className="space-y-1 max-h-[180px] overflow-y-auto pr-1">
          {events.map((ev) => (
            <div key={ev.id} className="flex items-center gap-2 text-xs">
              {eventIcon(ev)}
              <span className="flex-1 text-foreground/80">{eventLabel(ev)}</span>
              {deviceIcon(ev.eventData?.device as string | undefined)}
              <span className="text-muted-foreground flex-shrink-0">{timeSince(ev.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminNotesEditor({ user }: { user: AdminUser }) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user.adminNotes ?? "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [editing]);

  const saveNotesMutation = useMutation({
    mutationFn: ({ userId, notes }: { userId: string; notes: string | null }) =>
      apiRequest("PATCH", `/api/admin/users/${userId}/notes`, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setEditing(false);
      toast({ title: "Notes saved" });
    },
    onError: () => {
      toast({ title: "Failed to save notes", variant: "destructive" });
    },
  });

  const handleSave = () => {
    const trimmed = draft.trim() || null;
    saveNotesMutation.mutate({ userId: user.id, notes: trimmed });
  };

  const handleCancel = () => {
    setDraft(user.adminNotes ?? "");
    setEditing(false);
  };

  return (
    <div className="mt-3 pt-3 border-t" data-testid={`section-notes-${user.id}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <StickyNote className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs font-medium text-muted-foreground">Admin notes</p>
        {!editing && (
          <button
            onClick={() => { setDraft(user.adminNotes ?? ""); setEditing(true); }}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
            data-testid={`button-edit-notes-${user.id}`}
          >
            {user.adminNotes ? "Edit" : "Add note"}
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Emailed 2026-06-07 — suspected duplicate account, awaiting reply."
            className="text-xs min-h-[72px] resize-none"
            maxLength={2000}
            data-testid={`textarea-notes-${user.id}`}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={handleSave}
              disabled={saveNotesMutation.isPending}
              data-testid={`button-save-notes-${user.id}`}
            >
              <Check className="h-3.5 w-3.5" />
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs gap-1.5 text-muted-foreground"
              onClick={handleCancel}
              disabled={saveNotesMutation.isPending}
              data-testid={`button-cancel-notes-${user.id}`}
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </Button>
            {user.adminNotes && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
                onClick={() => saveNotesMutation.mutate({ userId: user.id, notes: null })}
                disabled={saveNotesMutation.isPending}
                data-testid={`button-clear-notes-${user.id}`}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      ) : user.adminNotes ? (
        <p className="text-xs text-foreground/80 whitespace-pre-wrap" data-testid={`text-notes-${user.id}`}>
          {user.adminNotes}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground italic">No notes yet.</p>
      )}
    </div>
  );
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getStatusBadge(status: string | null) {
  switch (status) {
    case "active":
      return <Badge variant="default" data-testid="badge-status-active">Active</Badge>;
    case "trial":
      return <Badge variant="secondary" data-testid="badge-status-trial">Trial</Badge>;
    case "expired":
      return <Badge variant="outline" data-testid="badge-status-expired">Expired</Badge>;
    case "inactive":
      return <Badge variant="outline" data-testid="badge-status-inactive">Inactive</Badge>;
    case "cancelled":
      return <Badge variant="destructive" data-testid="badge-status-cancelled">Cancelled</Badge>;
    default:
      return <Badge variant="outline" data-testid="badge-status-unknown">{status || "Unknown"}</Badge>;
  }
}

function getTrialInfo(user: AdminUser) {
  if (user.trialStatus === "active" && user.trialEndAt) {
    const end = new Date(user.trialEndAt);
    const now = new Date();
    const daysLeft = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    return `${daysLeft} days left`;
  }
  if (user.trialStatus === "expired") return "Expired";
  if (user.trialStatus === "used") return "Used";
  return null;
}

type TabFilter = "all" | "active" | "trial" | "inactive";

export default function AdminUsersPage() {
  const [product, setProduct] = useState<ProductFilter>("builder");
  const [emailRecipient, setEmailRecipient] = useState<EmailRecipient | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [bonusAmounts, setBonusAmounts] = useState<Record<string, number>>({});
  const [confirmDeleteGuestJourney, setConfirmDeleteGuestJourney] = useState<GuestQuoteJourney | null>(null);
  const [transferSource, setTransferSource] = useState<TransferSource | null>(null);

  const toggleExpanded = (userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };
  const { toast } = useToast();
  const openEmail = (user: AdminUser) => {
    if (!user.email) return;
    setEmailRecipient({
      id: user.id,
      email: user.email,
      name: [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email,
      product,
    });
  };

  const { data: users, isLoading } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/users", product],
    queryFn: () =>
      fetch(`/api/admin/users?product=${product}`, { credentials: "include" }).then((r) => {
        if (!r.ok) throw new Error("Failed to load users");
        return r.json();
      }),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    // Poll the heavier profile list only while a user's details are expanded.
    refetchInterval: expandedUsers.size > 0 ? 30_000 : false,
    refetchIntervalInBackground: false,
  });

  const {
    data: guestJourneys,
    isLoading: guestJourneysLoading,
    isError: guestJourneysError,
    error: guestJourneysFetchError,
    refetch: refetchGuestJourneys,
  } = useQuery<GuestQuoteJourney[]>({
    queryKey: ["/api/admin/guest-quote-journeys", product],
    queryFn: () =>
      fetch(`/api/admin/guest-quote-journeys?product=${product}`, { credentials: "include" }).then((r) => {
        if (!r.ok) throw new Error("Failed to load guest quote journeys");
        return r.json();
      }),
    // Guest rows can change when a visitor claims a quote. Do not retain the
    // global Infinity stale time for this admin attribution view.
    enabled: product === "builder",
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  interface LiveUser {
    userId: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    lastSeen: string;
    page: string | null;
  }

  const { data: liveUsers = [] } = useQuery<LiveUser[]>({
    queryKey: ["/api/admin/live-users"],
    refetchInterval: 15_000,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
  });

  const addBonusQuotesMutation = useMutation({
    mutationFn: ({ userId, amount }: { userId: string; amount: number }) =>
      apiRequest("POST", `/api/admin/users/${userId}/add-bonus-quotes`, { amount }),
    onSuccess: (_data, { userId, amount }) => {
      queryClient.setQueryData<AdminUser[]>(
        ["/api/admin/users"],
        (old) =>
          old?.map((u) =>
            u.id === userId ? { ...u, bonusQuotaCredits: (u.bonusQuotaCredits ?? 0) + amount } : u
          )
      );
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Bonus credits added", description: `Added ${amount} bonus Auto Quote credits to the user.` });
    },
    onError: () => {
      toast({ title: "Failed to add bonus credits", variant: "destructive" });
    },
  });

  const extendTrialMutation = useMutation({
    mutationFn: ({ userId, days }: { userId: string; days: number }) =>
      apiRequest("PATCH", `/api/admin/users/${userId}/extend-trial`, { days }),
    onSuccess: (_data, { days }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Trial extended", description: `Added ${days} days to the user's trial.` });
    },
    onError: () => {
      toast({ title: "Failed to extend trial", variant: "destructive" });
    },
  });

  const deleteGuestJourneyMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/admin/guest-quote-journeys/${id}`, {});
      return typeof (response as any)?.json === "function"
        ? await (response as any).json()
        : response as any;
    },
    onSuccess: (result: { status?: "deleted" | "accepted"; cleanupPending?: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/guest-quote-journeys", "builder"] });
      void refetchGuestJourneys();
      setConfirmDeleteGuestJourney(null);
      toast({
        title: result?.status === "accepted" ? "Test quote deletion queued" : "Test quote deleted",
        description: result?.status === "accepted"
          ? "The anonymous row is hidden while its uploaded files finish cleaning up."
          : "The anonymous guest journey was permanently removed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: error?.message || "Failed to delete test quote",
        variant: "destructive",
      });
    },
  });

  const syncStripeMutation = useMutation({
    mutationFn: (userId: string) =>
      apiRequest("POST", `/api/admin/users/${userId}/sync-stripe`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Subscription synced", description: "Status restored from Stripe." });
    },
    onError: () => {
      toast({ title: "Sync failed", variant: "destructive" });
    },
  });

  const enforceQuotaMutation = useMutation({
    mutationFn: ({ userId, enforced }: { userId: string; enforced: boolean }) =>
      apiRequest("PATCH", `/api/admin/users/${userId}/enforce-quota`, { enforced }),
    onSuccess: (_data, { enforced }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({
        title: enforced ? "Quote limit enforced" : "Quote restriction lifted",
        description: enforced
          ? "The user's free trial quote generation has been blocked."
          : "The user can generate quotes again.",
      });
    },
    onError: () => {
      toast({ title: "Failed to update quota enforcement", variant: "destructive" });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("DELETE", `/api/admin/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({
        title: "Deletion requested",
        description: "The account has been queued for review on the Account Deletions page. Confirm it there to anonymise the account.",
      });
    },
    onError: () => {
      toast({ title: "Failed to queue account deletion", variant: "destructive" });
    },
  });

  const sendReengagementMutation = useMutation({
    mutationFn: (userId: string) =>
      apiRequest("POST", `/api/admin/users/${userId}/send-reengagement`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "Re-engagement email sent", description: "The email has been sent to the user." });
    },
    onError: () => {
      toast({ title: "Failed to send email", variant: "destructive" });
    },
  });

  function handleDelete(user: AdminUser) {
    const name = user.firstName ? `${user.firstName} (${user.email})` : user.email;
    if (!window.confirm(`Queue ${name} for deletion? This creates a review request on the Account Deletions page — the account isn't anonymised until an admin confirms it there.`)) return;
    deleteUserMutation.mutate(user.id);
  }

  const anonymousTestJourneys = product === "builder"
    ? (guestJourneys ?? []).filter((journey) => journey.status === "anonymous")
    : [];
  const filteredRows: Array<
    | { kind: "user"; id: string; user: AdminUser }
    | { kind: "anonymous-test"; id: string; journey: GuestQuoteJourney }
  > = [
    ...(users || []).map((user) => ({ kind: "user" as const, id: user.id, user })),
    ...anonymousTestJourneys.map((journey) => ({
      kind: "anonymous-test" as const,
      id: `guest-${journey.id}`,
      journey,
    })),
  ].filter((row) => {
    if (row.kind === "anonymous-test") {
      if (activeTab !== "all") return false;
      return !searchQuery.trim() || "no user name".includes(searchQuery.trim().toLowerCase());
    }

    const u = row.user;
    if (activeTab === "active" && u.subscriptionStatus !== "active") return false;
    if (activeTab === "trial" && u.subscriptionStatus !== "trial") return false;
    if (activeTab === "inactive" && u.subscriptionStatus !== "inactive" && u.subscriptionStatus !== "expired" && u.subscriptionStatus !== "cancelled") return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
      const email = (u.email || "").toLowerCase();
      const company = (u.companyName || "").toLowerCase();
      return name.includes(q) || email.includes(q) || company.includes(q);
    }
    return true;
  }).sort((a, b) => {
    // Keep the combined list chronological. Anonymous journeys used to be
    // appended after every registered account, which made a newly generated
    // test quote look absent in a large Users table.
    const aCreatedAt = a.kind === "anonymous-test" ? a.journey.createdAt : a.user.createdAt;
    const bCreatedAt = b.kind === "anonymous-test" ? b.journey.createdAt : b.user.createdAt;
    const aTime = aCreatedAt ? new Date(aCreatedAt).getTime() : 0;
    const bTime = bCreatedAt ? new Date(bCreatedAt).getTime() : 0;
    return bTime - aTime;
  });

  const totalUsers = users?.length || 0;
  const activeUsers = users?.filter(u => u.subscriptionStatus === "active").length || 0;
  const trialUsers = users?.filter(u => u.subscriptionStatus === "trial").length || 0;
  const totalQuotes = users?.reduce((sum, u) => sum + u.quoteCount, 0) || 0;

  const tabs: { key: TabFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "trial", label: "Trial" },
    { key: "inactive", label: "Inactive" },
  ];

  if (isLoading || (product === "builder" && guestJourneysLoading)) {
    return (
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (product === "builder" && (guestJourneysError || guestJourneysFetchError)) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="guest-journeys-error">
        <Card>
          <CardHeader>
            <CardTitle>Guest quote tracking unavailable</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Anonymous test-quote rows could not be loaded, so this list is
              not showing an incomplete view. Try again to refresh tracking.
            </p>
            <Button
              variant="outline"
              onClick={() => void refetchGuestJourneys()}
              data-testid="button-retry-guest-journeys"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-lg sm:text-2xl font-bold" data-testid="text-admin-users-title">Registered Users</h1>
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1 self-start" data-testid="product-switcher">
          {([
            { key: "builder", label: "Builders", icon: Building2 },
            { key: "engineer", label: "Engineers", icon: HardHat },
          ] as { key: ProductFilter; label: string; icon: typeof Building2 }[]).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setProduct(key)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                product === key
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`button-product-${key}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="card-stat-total-users">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-users">{totalUsers}</div>
          </CardContent>
        </Card>
        <Card data-testid="card-stat-active-users">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Paid Subscriptions</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-active-users">{activeUsers}</div>
          </CardContent>
        </Card>
        <Card data-testid="card-stat-trial-users">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">On Trial</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-trial-users">{trialUsers}</div>
          </CardContent>
        </Card>
        <Card data-testid="card-stat-total-quotes">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Quotes</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-quotes">{totalQuotes}</div>
          </CardContent>
        </Card>
      </div>

      {/* Live users section */}
      <Card data-testid="card-live-users">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
            Live Now
            <Badge variant="secondary" className="ml-1 text-xs" data-testid="badge-live-count">
              {liveUsers.length} online
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Browser connection received within the last 60 seconds. An open tab does not necessarily mean the person is actively working.
          </p>
        </CardHeader>
        <CardContent>
          {liveUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="text-no-live-users">No users currently online.</p>
          ) : (
            <div className="space-y-2">
              {liveUsers.map((u) => {
                const name = u.firstName
                  ? `${u.firstName}${u.lastName ? " " + u.lastName : ""}`
                  : u.email;
                const seenAgo = Math.round(
                  (Date.now() - new Date(u.lastSeen).getTime()) / 1000
                );
                const seenLabel =
                  seenAgo < 5
                    ? "just now"
                    : seenAgo < 60
                    ? `${seenAgo}s ago`
                    : `${Math.floor(seenAgo / 60)}m ago`;
                return (
                  <div
                    key={u.userId}
                    className="flex items-center justify-between gap-3 text-sm"
                    data-testid={`row-live-user-${u.userId}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Circle className="h-2 w-2 fill-green-500 text-green-500 flex-shrink-0" />
                      <span className="font-medium truncate">{name}</span>
                      <span className="text-muted-foreground truncate hidden sm:inline">{u.email}</span>
                      {u.page && (
                        <Badge variant="outline" className="text-xs hidden md:inline-flex flex-shrink-0">
                          {pageLabel(u.page)}
                        </Badge>
                      )}
                    </div>
                    <span className="text-muted-foreground text-xs flex-shrink-0">{seenLabel}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => (
            <Button
              key={tab.key}
              size="sm"
              variant={activeTab === tab.key ? "default" : "ghost"}
              onClick={() => setActiveTab(tab.key)}
              className={activeTab === tab.key ? "toggle-elevate toggle-elevated" : ""}
              data-testid={`button-tab-${tab.key}`}
            >
              {tab.label}
            </Button>
          ))}
        </div>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-users"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium text-muted-foreground">User</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Company</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Trial</th>
                  <th className="text-center p-3 font-medium text-muted-foreground">Test</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Quotes</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">
                    <div className="flex items-center gap-1" title="Smart quotes">
                      <Wand2 className="h-3.5 w-3.5" />
                    </div>
                  </th>
                  <th className="text-left p-3 font-medium text-muted-foreground">
                    <div className="flex items-center gap-1" title="Template quotes">
                      <LayoutTemplate className="h-3.5 w-3.5" />
                    </div>
                  </th>
                  <th className="text-left p-3 font-medium text-muted-foreground">
                    <div className="flex items-center gap-1" title="Manual quotes">
                      <PenLine className="h-3.5 w-3.5" />
                    </div>
                  </th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-muted-foreground">
                      <UserX className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No users found</p>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map(row => {
                    if (row.kind === "anonymous-test") {
                      return (
                        <tr
                          key={row.id}
                          className="border-b"
                          data-testid={`row-anonymous-test-${row.journey.id}`}
                        >
                          <td className="p-3">
                            <div className="flex flex-col">
                              <span className="font-medium">No User Name</span>
                              <span className="text-xs text-muted-foreground">Test Quote Generated</span>
                            </div>
                          </td>
                          <td className="p-3 text-muted-foreground">--</td>
                          <td className="p-3 text-muted-foreground">--</td>
                          <td className="p-3 text-muted-foreground">--</td>
                          <td className="p-3 text-center">
                            <Check className="h-4 w-4 mx-auto text-green-600" aria-label="Test quote generated" />
                          </td>
                          <td className="p-3 text-muted-foreground">--</td>
                          <td className="p-3 text-muted-foreground">--</td>
                          <td className="p-3 text-muted-foreground">--</td>
                          <td className="p-3 text-muted-foreground">--</td>
                          <td className="p-3 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setConfirmDeleteGuestJourney(row.journey)}
                              disabled={deleteGuestJourneyMutation.isPending}
                              aria-label="Delete anonymous test quote"
                              title="Delete anonymous test quote"
                              data-testid={`button-delete-anonymous-test-${row.journey.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    }

                    const user = row.user;
                    const isExpanded = expandedUsers.has(user.id);
                    return (
                      <Fragment key={user.id}>
                        <tr className="border-b" data-testid={`row-admin-user-${user.id}`}>
                          <td className="p-3">
                            <div className="flex flex-col">
                              <span className="font-medium" data-testid={`text-user-name-${user.id}`}>
                                {user.firstName || user.lastName ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "No name"}
                              </span>
                              <span className="text-xs text-muted-foreground" data-testid={`text-user-email-${user.id}`}>
                                {user.email || "No email"}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            {user.companyName ? (
                              <div className="flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="truncate max-w-[140px]" data-testid={`text-user-company-${user.id}`}>{user.companyName}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">--</span>
                            )}
                          </td>
                          <td className="p-3" data-testid={`text-user-status-${user.id}`}>
                            {getStatusBadge(user.subscriptionStatus)}
                          </td>
                          <td className="p-3 text-muted-foreground">
                            {getTrialInfo(user) || "--"}
                          </td>
                          <td className="p-3 text-center">
                            {user.claimedTestQuoteCount > 0 && (
                              <Check
                                className="h-4 w-4 mx-auto text-green-600"
                                aria-label="Test quote generated"
                                data-testid={`icon-user-test-${user.id}`}
                              />
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1.5">
                              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                              <span data-testid={`text-user-quotes-${user.id}`}>{user.quoteCount}</span>
                            </div>
                          </td>
                          <td className="p-3 text-sm">{user.generatedAiQuoteCount}</td>
                          <td className="p-3 text-sm">{user.templateQuoteCount}</td>
                          <td className="p-3 text-sm">{user.manualQuoteCount}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-1.5">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEmail(user)}
                                disabled={!user.email}
                                title={user.email ? `Email ${user.email}` : "No email address on file"}
                                aria-label={user.email ? `Email ${user.email}` : "No email address on file"}
                                data-testid={`button-email-user-${user.id}`}
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                              {user.adminNotes && (
                                <span title="Admin notes recorded" data-testid={`icon-notes-${user.id}`}>
                                  <StickyNote className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                </span>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleExpanded(user.id)}
                                data-testid={`button-expand-user-${user.id}`}
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                              >
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${user.id}-expanded`} className="border-b bg-muted/30">
                            <td colSpan={10} className="px-4 py-3">
                              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                                <div className="flex items-center gap-1.5 text-sm">
                                  <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                  <span className="text-muted-foreground">Region:</span>
                                  <span className="capitalize">{user.region ? user.region.replace(/_/g, " ") : "--"}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-sm">
                                  <UserPlus className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                  <span className="text-muted-foreground">Referred by:</span>
                                  {user.referredByName ? (
                                    <span data-testid={`text-user-referrer-${user.id}`}>
                                      {user.referredByName}
                                      {user.referredByEmail && <span className="text-muted-foreground ml-1">({user.referredByEmail})</span>}
                                    </span>
                                  ) : (
                                    <span>--</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 text-sm">
                                  <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                  <span className="text-muted-foreground">Joined:</span>
                                  <span>{formatDate(user.createdAt)}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-sm">
                                  <Wifi className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                  <span className="text-muted-foreground">Last browser connection:</span>
                                  <span data-testid={`text-user-last-seen-${user.id}`}>{timeSince(user.lastSeenAt)}</span>
                                </div>
                                <div className="flex items-center gap-2 ml-auto">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEmail(user)}
                                    disabled={!user.email}
                                    data-testid={`button-email-profile-${user.id}`}
                                    title={user.email ? `Write a personal email to ${user.email}` : "No email address on file"}
                                  >
                                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                                    Email
                                  </Button>
                                  {user.bonusQuotaCredits > 0 && (
                                    <span
                                      className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700"
                                      data-testid={`badge-bonus-credits-${user.id}`}
                                      title="Non-expiring bonus Auto Quote credits remaining"
                                    >
                                      {user.bonusQuotaCredits} bonus
                                    </span>
                                  )}
                                  <button
                                    onClick={() => addBonusQuotesMutation.mutate({ userId: user.id, amount: bonusAmounts[user.id] ?? 10 })}
                                    disabled={addBonusQuotesMutation.isPending || deleteUserMutation.isPending}
                                    data-testid={`button-add-bonus-quotes-${user.id}`}
                                    className="inline-flex items-center gap-0.5 min-h-8 px-2 rounded-md border border-input bg-transparent text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:bg-accent disabled:opacity-50 whitespace-nowrap shadow-xs"
                                    title="Add non-expiring bonus Auto Quote credits"
                                  >
                                    <Wand2 className="h-3.5 w-3.5 mr-0.5" />
                                    Add +<input
                                      type="number"
                                      min={1}
                                      step={1}
                                      value={bonusAmounts[user.id] ?? 10}
                                      onClick={e => e.stopPropagation()}
                                      onChange={e => setBonusAmounts(prev => ({ ...prev, [user.id]: Math.max(1, Math.floor(Number(e.target.value) || 1)) }))}
                                      className="w-7 bg-transparent text-center focus:outline-none text-emerald-700 dark:text-emerald-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      disabled={addBonusQuotesMutation.isPending || deleteUserMutation.isPending}
                                    />
                                  </button>
                                  <div className="flex items-center gap-1.5">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => extendTrialMutation.mutate({ userId: user.id, days: 14 })}
                                      disabled={extendTrialMutation.isPending || deleteUserMutation.isPending || enforceQuotaMutation.isPending}
                                      data-testid={`button-extend-trial-${user.id}`}
                                      className="flex items-center gap-1.5 whitespace-nowrap text-[#5aabbf] border-[#5aabbf] hover:bg-[#5aabbf]/10 dark:hover:bg-[#5aabbf]/10"
                                    >
                                      <CalendarPlus className="h-3.5 w-3.5" />
                                      +14 days
                                    </Button>
                                    {(user.trialExtensionEmailOpenedAt || user.trialExtensionEmailClickedAt) && (
                                      <span className="flex flex-col gap-0.5">
                                        {user.trialExtensionEmailOpenedAt && (
                                          <span
                                            className="text-xs whitespace-nowrap text-green-600"
                                            title={`Opened ${new Date(user.trialExtensionEmailOpenedAt).toLocaleString("en-GB")}`}
                                          >
                                            Opened {timeSince(user.trialExtensionEmailOpenedAt)}
                                          </span>
                                        )}
                                        {user.trialExtensionEmailClickedAt && (
                                          <span
                                            className="text-xs whitespace-nowrap text-green-600"
                                            title={`Clicked PDF ${new Date(user.trialExtensionEmailClickedAt).toLocaleString("en-GB")}`}
                                          >
                                            Clicked {timeSince(user.trialExtensionEmailClickedAt)}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  {product === "builder" && user.stripeSubscriptionId && user.subscriptionStatus === "active" && (
                                    <Button size="sm" variant="outline" onClick={() => setTransferSource(user)}
                                      disabled={deleteUserMutation.isPending}
                                      className="flex items-center gap-1.5 whitespace-nowrap"
                                      data-testid={`button-transfer-subscription-${user.id}`}>
                                      <ArrowRightLeft className="h-3.5 w-3.5" />
                                      Transfer subscription
                                    </Button>
                                  )}
                                  {user.stripeSubscriptionId && user.subscriptionStatus !== "active" && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => syncStripeMutation.mutate(user.id)}
                                      disabled={syncStripeMutation.isPending || deleteUserMutation.isPending}
                                      className="flex items-center gap-1.5 whitespace-nowrap text-green-600 border-green-300 hover:bg-green-50 dark:hover:bg-green-950"
                                      title="Restore subscription status from Stripe"
                                    >
                                      <RefreshCw className="h-3.5 w-3.5" />
                                      Sync Stripe
                                    </Button>
                                  )}
                                  {user.quotaLimitEnforced ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => enforceQuotaMutation.mutate({ userId: user.id, enforced: false })}
                                      disabled={enforceQuotaMutation.isPending || deleteUserMutation.isPending}
                                      data-testid={`button-lift-quota-${user.id}`}
                                      className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground"
                                      title="Lift the admin-enforced quote restriction for this user"
                                    >
                                      <ShieldOff className="h-3.5 w-3.5" />
                                      Lift Quote Restriction
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => enforceQuotaMutation.mutate({ userId: user.id, enforced: true })}
                                      disabled={enforceQuotaMutation.isPending || deleteUserMutation.isPending}
                                      data-testid={`button-enforce-quota-${user.id}`}
                                      className="flex items-center gap-1.5 whitespace-nowrap text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950"
                                      title="Apply to each account separately for linked users"
                                    >
                                      <Ban className="h-3.5 w-3.5" />
                                      End Free Trial
                                    </Button>
                                  )}
                                  <div className="flex items-center gap-1.5">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => sendReengagementMutation.mutate(user.id)}
                                      disabled={sendReengagementMutation.isPending || deleteUserMutation.isPending}
                                      data-testid={`button-send-reengagement-${user.id}`}
                                      className="flex items-center gap-1.5 whitespace-nowrap"
                                      title="Send a re-engagement email with a doubled-quota upgrade incentive"
                                    >
                                      <Mail className="h-3.5 w-3.5" />
                                      Re-engage
                                    </Button>
                                    {user.reengagementEmailSentAt && (
                                      <span
                                        className={`text-xs whitespace-nowrap ${user.reengagementEmailOpenedAt ? "text-green-600" : "text-amber-600"}`}
                                        title={user.reengagementEmailOpenedAt
                                          ? `Opened ${new Date(user.reengagementEmailOpenedAt).toLocaleString("en-GB")}`
                                          : `Sent ${new Date(user.reengagementEmailSentAt).toLocaleString("en-GB")}`}
                                      >
                                        {user.reengagementEmailOpenedAt
                                          ? `Opened ${timeSince(user.reengagementEmailOpenedAt)}`
                                          : `Sent ${timeSince(user.reengagementEmailSentAt)}`}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Button
                                      asChild
                                      size="sm"
                                      variant="outline"
                                      className="flex items-center gap-1.5 whitespace-nowrap"
                                    >
                                      <Link
                                        href={`/admin/messages?type=first_quote&userId=${encodeURIComponent(user.id)}`}
                                        data-testid={`link-first-quote-message-${user.id}`}
                                        title="Open First Quote Messages in Message Centre with this user preselected (if eligible)"
                                      >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        First quote
                                      </Link>
                                    </Button>
                                    {user.firstQuoteNudgeEmailSentAt && (
                                      <span className="flex flex-col gap-0.5">
                                        <span
                                          className={`text-xs whitespace-nowrap ${user.firstQuoteNudgeEmailOpenedAt ? "text-green-600" : "text-amber-600"}`}
                                          title={user.firstQuoteNudgeEmailOpenedAt
                                            ? `Opened ${new Date(user.firstQuoteNudgeEmailOpenedAt).toLocaleString("en-GB")}`
                                            : `Sent ${new Date(user.firstQuoteNudgeEmailSentAt).toLocaleString("en-GB")}`}
                                        >
                                          {user.firstQuoteNudgeEmailOpenedAt
                                            ? `Opened ${timeSince(user.firstQuoteNudgeEmailOpenedAt)}`
                                            : `Sent ${timeSince(user.firstQuoteNudgeEmailSentAt)}`}
                                        </span>
                                        {user.firstQuoteNudgeEmailClickedAt && (
                                          <span
                                            className="text-xs whitespace-nowrap text-green-600"
                                            title={`Clicked PDF ${new Date(user.firstQuoteNudgeEmailClickedAt).toLocaleString("en-GB")}`}
                                          >
                                            Clicked {timeSince(user.firstQuoteNudgeEmailClickedAt)}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDelete(user)}
                                    disabled={deleteUserMutation.isPending}
                                    data-testid={`button-delete-user-${user.id}`}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                              <AdminNotesEditor user={user} />
                              <UserActivitySection userId={user.id} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <AdminComposeEmailDialog recipient={emailRecipient} onClose={() => setEmailRecipient(null)} />
      {transferSource && <AdminSubscriptionTransferDialog source={transferSource} onClose={() => setTransferSource(null)} />}

      <AlertDialog
        open={!!confirmDeleteGuestJourney}
        onOpenChange={(open) => !open && setConfirmDeleteGuestJourney(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this anonymous test quote?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the generated guest quote and its uploaded files. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-anonymous-test">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDeleteGuestJourney && deleteGuestJourneyMutation.mutate(confirmDeleteGuestJourney.id)}
              disabled={deleteGuestJourneyMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-anonymous-test"
            >
              {deleteGuestJourneyMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
